import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import MainLayout from '../layouts/MainLayout';
import SEOHead from '../components/SEOHead';
import StructuredData, { generateEventSchema } from '../components/StructuredData';
import { getEvent, getImageUrl, getVideoUrl } from '../utils/contentful';

// Fix for Leaflet marker icon
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const EventDetailsPage = () => {
  const { eventId } = useParams();
  const [activeImage, setActiveImage] = useState(0);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const eventData = await getEvent(eventId);
        setEvent(eventData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load event details');
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <p className="text-gray-600">Зареждане...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !event) {
    return (
      <MainLayout>
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <p className="text-red-600">{error || 'Event not found'}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const { fields } = event;
  const eventDate = new Date(fields.date);
  const formattedDateTime = eventDate.toLocaleString('bg-BG', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  const isPastEvent = eventDate <= new Date();

  // Gallery navigation functions
  const nextImage = () => {
    setActiveImage((activeImage + 1) % fields.images.length);
  };

  const prevImage = () => {
    setActiveImage((activeImage - 1 + fields.images.length) % fields.images.length);
  };

  // For a past event, show different content
  const pastEventContent = isPastEvent && (fields.collectedWaste || fields.participants) ? (
    <div className="bg-white p-6 rounded-lg shadow-md mb-8">
      <h2 className="text-xl font-bold text-green-800 mb-4">Резултати</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Gallery in Results */}
        {fields.images && fields.images.length > 1 && (
          <div>
            <div className="relative">
              <div className="w-full h-64 bg-gray-200 rounded-lg overflow-hidden">
                <img 
                  src={getImageUrl(fields.images[activeImage])} 
                  alt={`Gallery ${activeImage + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <button 
                onClick={prevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
                aria-label="Previous image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button 
                onClick={nextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity"
                aria-label="Next image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              <div className="flex justify-center mt-4">
                {fields.images.map((_, index) => (
                  <button 
                    key={index}
                    onClick={() => setActiveImage(index)}
                    className={`h-2 w-2 mx-1 rounded-full ${index === activeImage ? 'bg-green-600' : 'bg-gray-300'}`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        
        <div>
          <p className="text-gray-700 mb-6">
            Благодарим на всички, които се присъединиха към нас!
          </p>
          <div className="space-y-4">
            {fields.collectedWaste && (
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-800">{fields.collectedWaste} кг</p>
                  <p className="text-gray-600">Събрани отпадъци</p>
                </div>
              </div>
            )}
            {fields.participants && (
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-800">{fields.participants}</p>
                  <p className="text-gray-600">Участници</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <MainLayout>
      <SEOHead 
        title={`${fields.title} - Refresh Bulgaria`}
        description={`${fields.description?.substring(0, 150)}... Присъединете се към доброволческа акция на Refresh Bulgaria на ${fields.location}.`}
        keywords={`${fields.title}, еко акция, почистване ${fields.location}, доброволчество България`}
        url={`https://refreshbulgaria.com/events/${eventId}`}
        image={fields.mainImage ? getImageUrl(fields.mainImage) : '/img/logo.png'}
        type="article"
      />
      <StructuredData data={generateEventSchema(event)} />
      <div className="max-w-screen-xl mx-auto px-4">
        {/* Main content */}
        <main className="mt-4">
          {/* Back to Events Button */}
          <div className="mb-6">
            <Link 
              to="/events"
              state={{ activeTab: isPastEvent ? 'past' : 'upcoming' }}
              className="inline-flex items-center text-green-700 hover:text-green-600 font-semibold text-lg transition-colors duration-200"
            >
              ← {isPastEvent ? 'Всички минали събития' : 'Всички предстоящи събития'}
            </Link>
          </div>

          {/* Hero Image */}
          {fields.mainImage && (
            <section className="mb-8">
              <div className="w-full h-64 md:h-96 bg-gray-200 rounded-lg overflow-hidden">
                <img 
                  src={getImageUrl(fields.mainImage)} 
                  alt={fields.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </section>
          )}

          {/* Event Details */}
          <section className="mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-green-800">{fields.title}</h1>
                <span className={`px-4 py-1 rounded-lg text-white ${isPastEvent ? 'bg-gray-600' : 'bg-green-600'}`}>
                  {isPastEvent ? 'Минало' : 'Предстоящо'}
                </span>
              </div>
              
              <div className={fields.video ? "flex flex-col lg:flex-row gap-8 items-start" : ""}>
                <div className={fields.video ? "lg:w-1/2" : ""}>
                  <div className="flex items-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-gray-700 text-lg font-medium">{fields.locationText}</p>
                  </div>
                  
                  <div className="flex items-center mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-gray-700 text-lg font-medium">{formattedDateTime}</p>
                  </div>

                  <div className="border-t pt-6">
                    <h2 className="text-xl font-bold text-green-800 mb-4">За събитието</h2>
                    <p className="text-gray-700">{fields.description}</p>
                  </div>
                </div>
                
                {fields.video && (
                  <div className="lg:w-1/2">
                    <div className="rounded-lg aspect-[3/4] overflow-hidden shadow-lg">
                      <video 
                        src={getVideoUrl(fields.video)}
                        controls
                        className="w-full h-full object-cover"
                        title={`Video for ${fields.title}`}
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Location Map */}
          {fields.location && !isPastEvent && (
            <section className="mb-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-bold text-green-800 mb-4">Локация</h2>
                <div className="w-full h-80 rounded-lg overflow-hidden relative z-0">
                  <MapContainer 
                    center={[fields.location.lat, fields.location.lon]} 
                    zoom={13} 
                    style={{ height: '100%', width: '100%', zIndex: 1 }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[fields.location.lat, fields.location.lon]}>
                      <Popup>
                        {fields.location.lat}, {fields.location.lon}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>
            </section>
          )}




          {/* Conditional content based on event status */}
          {pastEventContent}

        </main>
      </div>
    </MainLayout>
  );
};

export default EventDetailsPage; 